import { ArrowUpRight, Scale, Sprout, Sparkles, Check, Quote } from "lucide-react";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import About from "./About";
import Difference from "./Difference";
import RelatedPages from "./RelatedPages";
import PullQuote from "./PullQuote";
import Reveal from "./Reveal";
import SectionOrnament from "./SectionOrnament";
import { aboutCopy, knowUsExtra } from "../mock";

const AIM_ICONS = {
  Balance: Scale,
  Sustain: Sprout,
  Inspire: Sparkles,
};

export default function KnowUsPage() {
  const { aims, principles, leaders } = knowUsExtra;

  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero
            eyebrow="Know Us"
            title="Know"
            italic="GD Goenka Signature"
            image={aboutCopy.image}
            blurb="A premium CBSE day, weekly and full boarding school on a 20-acre signature campus in Sohna Road, Gurugram — just 60 minutes from the New Delhi airport."
          />

          {/* About — re-used from home */}
          <About />

          {/* Aims & Values */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1100px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-10 md:mb-12">
                  <div className="eyebrow mb-4">Aims &amp; Values</div>
                  <h2 className="h-section text-[var(--ink)]">
                    A haven for
                    <span className="serif-italic gold-text"> holistic learning.</span>
                  </h2>
                  <p className="mt-5 text-[15px] md:text-[17px] leading-[1.85] text-[var(--ink)]/75 serif">
                    Carried out under the supervision of mentors who are masters of their respective fields — rooted in firm values and clear aims for the future.
                  </p>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {aims.map((a, i) => {
                  const Icon = AIM_ICONS[a.label] || Sparkles;
                  return (
                    <Reveal key={a.label} variant="zoom" delay={i * 120}>
                      <div className="text-center p-7 md:p-9 light-card h-full">
                        <span className="inline-flex w-14 h-14 items-center justify-center border border-[var(--gold)] rotate-45">
                          <Icon size={22} className="text-[var(--gold)] -rotate-45" strokeWidth={1.4} />
                        </span>
                        <h3 className="serif text-[28px] md:text-[34px] text-[var(--ink)] mt-5">
                          {a.label}
                        </h3>
                        <span className="gold-divider-thin block mx-auto my-4" />
                        <p className="text-[14.5px] leading-[1.75] text-[var(--ink)]/75 max-w-[280px] mx-auto">{a.body}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Operating Principles */}
          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Operating Principles</div>
                  <h2 className="h-section text-[var(--ink)]">
                    The quiet philosophies
                    <span className="block serif-italic gold-text">that shape the school.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {principles.map((p, i) => (
                  <Reveal key={p.title} delay={(i % 2) * 100}>
                    <article className="p-6 md:p-8 border border-[rgba(184,148,86,0.25)] light-card h-full">
                      <div className="text-[12px] tracking-[0.28em] uppercase text-[var(--gold)]">0{i + 1}</div>
                      <h3 className="serif text-[22px] md:text-[26px] text-[var(--ink)] leading-tight mt-2">
                        {p.title}
                      </h3>
                      <span className="gold-divider-thin block my-4" />
                      <p className="text-[14.5px] md:text-[15.5px] leading-[1.8] text-[var(--ink)]/80">{p.body}</p>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <PullQuote
            tone="dark"
            text="A school is not a building. It is a heartbeat — of children laughing, of ideas colliding, of futures being shaped, every single day."
          />

          {/* Management & Leadership */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1300px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[760px] mx-auto mb-10 md:mb-14">
                  <div className="eyebrow mb-4">Management &amp; Leadership</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Mentors who shape
                    <span className="serif-italic gold-text"> the school.</span>
                  </h2>
                  <span className="gold-divider mx-auto" />
                </div>
              </Reveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {leaders.map((l, i) => (
                  <Reveal key={l.name} delay={(i % 3) * 100} variant="zoom">
                    <article className="light-card overflow-hidden h-full flex flex-col bg-white">
                      <div className="aspect-[4/5] overflow-hidden bg-[var(--navy)]">
                        <img src={l.image} alt={l.name} loading="lazy" className="w-full h-full object-cover img-lift" />
                      </div>
                      <div className="p-5 md:p-6 flex-1 flex flex-col">
                        <div className="eyebrow">{l.role}</div>
                        <h3 className="serif text-[20px] md:text-[24px] text-[var(--ink)] leading-tight mt-1.5">
                          {l.name}
                        </h3>
                        <span className="gold-divider-thin block my-3.5" />
                        <p className="text-[13.5px] md:text-[14px] leading-[1.75] text-[var(--ink)]/75 flex-1">{l.body}</p>
                      </div>
                    </article>
                  </Reveal>
                ))}
              </div>

              <Reveal>
                <div className="mt-12 md:mt-14 text-center">
                  <button onClick={openEnquire} className="btn-gold inline-flex items-center gap-2" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Meet us in person <ArrowUpRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Difference — what makes us, reused from home */}
          <Difference />

          <RelatedPages current="/know-us.html" />
        </>
      )}
    </PageShell>
  );
}
